import React, { JSX, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { observer, useLocalStore } from 'mobx-react';
import {
  Error,
  MenuItem,
  packageCartStoreContext,
  useAuth,
  useBookingService,
  useConfig,
  useMMBBookingService
} from '@ibe/components';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Button, Col, Media, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import Keys from '../../Translations/generated/header.en.json.keys';
// eslint-disable-next-line import/no-cycle
import { b2bPages, manageBookingPages, PageUrl } from '../../pages';
import { useMount } from 'react-use';
import { LocalStorageKey } from '../../Storage/LocalStorageKeyRegistry';
import PageUtil from '../../Util/PageUtil';
import { ApiImageContainer, ApiIncludedService, ApiPackageModel, ApiRole } from '@ibe/api';
import AgencyMenu from '../../Components/AgencyMenu/AgencyMenu';
import WorkflowTabs from '../../Components/WorkflowTabs/WorkflowTabs';
import useCms from '../../Hooks/useCms';
import useAuthSessionPersistence from '../../Hooks/useAuthSessionPersistence';
import CurrencySelector from '../../Components/CurrencySelector/CurrencySelector';
import MegConfigModel from '../../Config/MegConfigModel';
import { useTranslation } from 'react-i18next';
import { BreadcrumbProps } from '../../Components/MMB/NavigationBreadcrumb/NavigationBreadcrumb';
import useBookingFeatureStore from '../../Hooks/useBookingFeatureStore';
import ImageProgressbar from '../../Components/ImageProgressbar/ImageProgressbar';
import useGlobalElementsService from '../../Hooks/useGlobalElementsService';

const SummaryPopoverWithActions = React.lazy(
  () => import('../../Components/SummaryPopoverWithActions/SummaryPopoverWithActions')
);

const Header = observer(
  ({
    breadcrumbProps,
    onGoToCheckout,
    editBookingItemExtension,
    packageIncludedServices,
    packageModel,
    showHeader,
    isAddExtraWorkflow
  }: {
    breadcrumbProps?: BreadcrumbProps;
    onGoToCheckout?: () => Promise<void>;
    editBookingItemExtension?: (itemIDs: Array<string>) => void;
    packageIncludedServices?: Array<ApiIncludedService>;
    packageModel?: ApiPackageModel;
    showHeader: boolean;
    isAddExtraWorkflow: boolean;
  }) => {
    const { t } = useTranslation('header');
    const { authState } = useAuth();
    const navigate = useNavigate();
    const config = useConfig() as MegConfigModel;
    const location = useLocation();
    const [showNavigationBackModal, setShowNavigationBackModal] = useState<boolean>(false);
    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
    const [showWorkflowTabs, setShowWorkflowTabs] = useState<boolean>(false);
    const [prevPagePath] = useAuthSessionPersistence<string>(LocalStorageKey.PAGE_URL);
    const packageCartContext = useContext(packageCartStoreContext);
    const packageCartStore = useLocalStore(() => packageCartContext);
    const cms = useCms();
    const mmbBookingService = useMMBBookingService();
    const bookingService = useBookingService();
    const bookingFeatureStore = useBookingFeatureStore();
    const cmsGlobalElementsService = useGlobalElementsService();
    const [logo, setLogo] = useState<ApiImageContainer>();
    const [headerHref, setHeaderHref] = useState<string | undefined>();

    const logoImage = logo?.small?.url ? (
      <Media
        src={logo.small?.url}
        alt={logo.imageAlt ?? 'header-logo'}
        className="customizable-header__logo"
      />
    ) : null;

    let wrappedLogo = null;

    if (logoImage) {
      wrappedLogo = headerHref ? (
        <a
          href={headerHref}
          className="customizable-header__logo-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          {logoImage}
        </a>
      ) : (
        <div className="customizable-header__logo-wrapper">{logoImage}</div>
      );
    }

    function onErrorAction() {
      setShowErrorModal(true);
    }

    useMount(() => {
      const currentPath = location.pathname;
      const noNavigationBackModal =
        location.state?.noNavigationBackModal ||
        manageBookingPages.includes(currentPath as PageUrl);
      if (
        !noNavigationBackModal &&
        !PageUtil.getShoppingCartPagePaths().includes(currentPath) &&
        prevPagePath &&
        PageUtil.getShoppingCartPagePaths().includes(prevPagePath) &&
        packageCartStore.cart
      ) {
        setShowNavigationBackModal(true);
      }
    });

    useEffect((): void => {
      if (showHeader && !isAddExtraWorkflow) {
        cmsGlobalElementsService.fetchGlobalElements().then(ge => {
          setLogo(ge.headerImage);
          setHeaderHref(ge.headerHref);
        });
      }
      if (manageBookingPages.includes(location.pathname as PageUrl)) {
        setShowWorkflowTabs(false);
      } else {
        setShowWorkflowTabs(
          cms.workflows.length > 1 || b2bPages.includes(location.pathname as PageUrl)
        );
      }
    }, [
      cms.workflows,
      location,
      mmbBookingService.booking,
      bookingFeatureStore.cancellableIntoVoucher,
      bookingFeatureStore.cancellable,
      showHeader,
      isAddExtraWorkflow
    ]);

    const shouldShowShoppingCart = useMemo((): boolean => {
      return (
        ![
          ...manageBookingPages,
          PageUrl.CHECKOUT,
          PageUrl.HOTEL_LIST,
          PageUrl.PACKAGE,
          PageUrl.PAYMENT,
          PageUrl.CONFIRMATION
        ].includes(location.pathname as PageUrl) &&
        (!!bookingService.booking || packageCartStore.hasCart)
      );
    }, [location.pathname, !!bookingService.booking, packageCartStore.hasCart]);

    function isUserLoggedIn(): boolean {
      return !!authState.user && !authState.isGuest && !authState.agency?.number;
    }

    const Logout = (): JSX.Element => (
      <div className="logout-link__container">
        <a
          className="logout-link"
          onClick={(): void => {
            navigate(PageUrl.LOGOUT);
          }}
        >
          <span className="mx-2 px-2">{t(Keys.Logout)}</span>
        </a>
      </div>
    );

    return showHeader ? (
      <div className={'header-container'}>
        <div
          className={`customizable-header align-items-center justify-content-${
            logoImage ? 'between' : 'end'
          }`}
        >
          {wrappedLogo}
          <div className="d-flex flex-column">
            {isUserLoggedIn() && !isAddExtraWorkflow && <Logout />}
            {shouldShowShoppingCart && (
              <SummaryPopoverWithActions
                header={sessionStorage.getItem('PACKAGE_NAME') || undefined}
                onGoToCheckout={onGoToCheckout}
                editBookingItemExtension={editBookingItemExtension}
                // onBookingChange={(bookingExist: boolean) => setIsCartDisplayed(bookingExist)}
                packageIncludedServices={packageIncludedServices}
                onErrorAction={onErrorAction}
                packageModel={packageModel}
              />
            )}
            {authState.isAgency &&
              (authState.role === ApiRole.AGENT || authState.role === ApiRole.AGENCYOWNER) && (
                <div className="d-flex align-items-center justify-content-end">
                  <AgencyMenu
                    userName={
                      authState.user ? `${authState.user.firstName} ${authState.user.lastName}` : ''
                    }
                  />
                </div>
              )}
          </div>
        </div>
        {showWorkflowTabs && <WorkflowTabs />}
        <div className="d-flex align-items-center justify-content-end">
          {config.showCurrencySelection && <CurrencySelector />}
        </div>
        {!!breadcrumbProps && (
          <ImageProgressbar
            steps={breadcrumbProps.items.map((step: MenuItem, idx: number) => ({
              description: step.label,
              image: faArrowRight,
              selected: idx === breadcrumbProps.activeIndex
            }))}
            showNotActiveIcon
            onClick={(stepIndex: number, description: string) => {
              breadcrumbProps.onClickAction(
                // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
                breadcrumbProps.items.find(item => item.label === description)!,
                stepIndex
              );
            }}
          />
        )}
        <Modal
          className="header_navigationBack-modal"
          isOpen={showNavigationBackModal}
          backdrop={true}
          toggle={() => {
            setShowNavigationBackModal(!showNavigationBackModal);
            packageCartStore.resetCart();
          }}
          container={document.getElementById('iso') || undefined}
        >
          <ModalHeader>{t(Keys.navigationBackModal.header)}</ModalHeader>
          <ModalBody id="header_navigationBack-modal">
            {t(Keys.navigationBackModal.content)}
          </ModalBody>
          <ModalFooter>
            <Row className="justify-content-end">
              <Col xs={12} md={'auto'} className="mb-2">
                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  onClick={(): void => {
                    setShowNavigationBackModal(false);
                    packageCartStore.resetCart();
                  }}
                >
                  {t(Keys.navigationBackModal.back)}
                </button>
              </Col>
            </Row>
          </ModalFooter>
        </Modal>

        <Modal
          className="hotelDetails-modal"
          isOpen={showErrorModal}
          backdrop={true}
          container={document.getElementById('iso') || undefined}
        >
          <ModalBody id={'hotelDetails-modal'}>
            <Row>
              <Col>
                <Row className="justify-content-center">
                  <div className="text-center px-2 py-1">
                    <Error message={t(Keys.errorModalInformation)} />
                  </div>
                </Row>
                <Row className="justify-content-center">
                  <Button
                    className="btn-primary"
                    onClick={(): void => {
                      setShowErrorModal(false);
                    }}
                  >
                    <div className="text-center px-2 py-1">{t(Keys.errorModalButtonLabel)}</div>
                  </Button>
                </Row>
              </Col>
            </Row>
          </ModalBody>
        </Modal>
      </div>
    ) : null;
  }
);

export default Header;
